import {
  createContext,
  forwardRef,
  useContext,
  useId,
  type InputHTMLAttributes,
  type ReactNode,
  type SelectHTMLAttributes,
  type TextareaHTMLAttributes,
} from 'react';
import { Icon, type IconName } from '../icons';

// Form controls. Every control accepts `invalid` (sets aria-invalid + the error ring) and
// takes its id from the surrounding <Field> when one is present, so label/help/error
// wiring is automatic.

interface FieldCtx {
  id: string;
  helpId?: string;
  errorId?: string;
  invalid: boolean;
  disabled?: boolean;
}
const FieldContext = createContext<FieldCtx | null>(null);

export function useFieldCtx(): FieldCtx | null {
  return useContext(FieldContext);
}

/** Wires label ↔ control ↔ help ↔ error with ids and aria attributes. */
export function Field({
  label,
  help,
  error,
  required,
  optional,
  children,
  inline,
  className,
  id: idProp,
  disabled,
  span,
}: {
  label?: ReactNode;
  help?: ReactNode;
  error?: ReactNode;
  required?: boolean;
  optional?: boolean;
  children: ReactNode;
  inline?: boolean;
  className?: string;
  id?: string;
  disabled?: boolean;
  /** Grid span inside .hf-form-grid. */
  span?: 1 | 2;
}) {
  const auto = useId();
  const id = idProp ?? `f-${auto}`;
  const helpId = help ? `${id}-help` : undefined;
  const errorId = error ? `${id}-err` : undefined;
  const ctx: FieldCtx = { id, helpId, errorId, invalid: Boolean(error), disabled };
  return (
    <FieldContext.Provider value={ctx}>
      <div className={['hf-field', inline && 'inline', span === 2 && 'span-2', className].filter(Boolean).join(' ')}>
        {label != null && (
          <Label htmlFor={id} required={required} optional={optional}>
            {label}
          </Label>
        )}
        {children}
        {help && !error && <Help id={helpId}>{help}</Help>}
        {error && <ErrorText id={errorId}>{error}</ErrorText>}
      </div>
    </FieldContext.Provider>
  );
}

export function Label({
  htmlFor,
  required,
  optional,
  children,
}: {
  htmlFor?: string;
  required?: boolean;
  optional?: boolean;
  children: ReactNode;
}) {
  return (
    <label className="hf-label" htmlFor={htmlFor}>
      {children}
      {required && (
        <span className="req" aria-hidden>
          *
        </span>
      )}
      {optional && <span className="opt">(optional)</span>}
    </label>
  );
}

export function Help({ id, children }: { id?: string; children: ReactNode }) {
  return (
    <div className="hf-help" id={id}>
      {children}
    </div>
  );
}

export function ErrorText({ id, children }: { id?: string; children: ReactNode }) {
  return (
    <div className="hf-error" id={id} role="alert">
      <Icon name="alert-circle" size={12} />
      {children}
    </div>
  );
}

function aria(ctx: FieldCtx | null, invalid?: boolean) {
  const bad = invalid ?? ctx?.invalid ?? false;
  const describedBy = [ctx?.errorId, ctx?.helpId].filter(Boolean).join(' ') || undefined;
  return { 'aria-invalid': bad || undefined, 'aria-describedby': describedBy };
}

export interface InputProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'size' | 'prefix'> {
  invalid?: boolean;
  size?: 'sm' | 'md';
  /** Prefix text (e.g. currency code) or icon. */
  prefix?: ReactNode;
  prefixIcon?: IconName;
  suffix?: ReactNode;
  numeric?: boolean;
  bare?: boolean;
}

export const Input = forwardRef<HTMLInputElement, InputProps>(function Input(
  { invalid, size = 'md', prefix, prefixIcon, suffix, numeric, bare, className, id, disabled, ...rest },
  ref,
) {
  const ctx = useFieldCtx();
  const cls = ['hf-input', size === 'sm' && 'sm', numeric && 'num', bare && 'bare', className].filter(Boolean).join(' ');
  const input = (
    <input
      ref={ref}
      id={id ?? ctx?.id}
      className={cls}
      disabled={disabled ?? ctx?.disabled}
      {...aria(ctx, invalid)}
      {...rest}
    />
  );
  if (prefix == null && prefixIcon == null && suffix == null) return input;
  return (
    <div className={['hf-input-wrap', (invalid ?? ctx?.invalid) && 'invalid'].filter(Boolean).join(' ')}>
      {prefixIcon && (
        <span className="affix icon">
          <Icon name={prefixIcon} size={14} />
        </span>
      )}
      {prefix != null && <span className="affix">{prefix}</span>}
      {input}
      {suffix != null && <span className="affix end">{suffix}</span>}
    </div>
  );
});

export interface TextareaProps extends TextareaHTMLAttributes<HTMLTextAreaElement> {
  invalid?: boolean;
}
export const Textarea = forwardRef<HTMLTextAreaElement, TextareaProps>(function Textarea({ invalid, className, id, disabled, ...rest }, ref) {
  const ctx = useFieldCtx();
  return (
    <textarea
      ref={ref}
      id={id ?? ctx?.id}
      className={['hf-textarea', className].filter(Boolean).join(' ')}
      disabled={disabled ?? ctx?.disabled}
      {...aria(ctx, invalid)}
      {...rest}
    />
  );
});

export interface SelectOption {
  value: string;
  label: string;
  disabled?: boolean;
}
export interface SelectProps extends Omit<SelectHTMLAttributes<HTMLSelectElement>, 'size'> {
  invalid?: boolean;
  size?: 'sm' | 'md';
  options?: SelectOption[];
  placeholder?: string;
}
export const Select = forwardRef<HTMLSelectElement, SelectProps>(function Select(
  { invalid, size = 'md', options, placeholder, className, id, disabled, children, ...rest },
  ref,
) {
  const ctx = useFieldCtx();
  return (
    <div className="hf-select-wrap">
      <select
        ref={ref}
        id={id ?? ctx?.id}
        className={['hf-select', size === 'sm' && 'sm', className].filter(Boolean).join(' ')}
        disabled={disabled ?? ctx?.disabled}
        {...aria(ctx, invalid)}
        {...rest}
      >
        {placeholder != null && <option value="">{placeholder}</option>}
        {options?.map((o) => (
          <option key={o.value} value={o.value} disabled={o.disabled}>
            {o.label}
          </option>
        ))}
        {children}
      </select>
      <Icon name="chevron-down" size={14} />
    </div>
  );
});

export interface CheckboxProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'type'> {
  label?: ReactNode;
  indeterminate?: boolean;
}
export const Checkbox = forwardRef<HTMLInputElement, CheckboxProps>(function Checkbox({ label, indeterminate, className, id, disabled, ...rest }, ref) {
  const ctx = useFieldCtx();
  return (
    <label className={['hf-check', (disabled ?? ctx?.disabled) && 'disabled', className].filter(Boolean).join(' ')}>
      <input
        type="checkbox"
        ref={(el) => {
          if (el) el.indeterminate = Boolean(indeterminate);
          if (typeof ref === 'function') ref(el);
          else if (ref) ref.current = el;
        }}
        id={id ?? ctx?.id}
        disabled={disabled ?? ctx?.disabled}
        {...rest}
      />
      <span className="box" aria-hidden>
        <Icon name={indeterminate ? 'minus' : 'check'} size={12} weight={3} />
      </span>
      {label != null && <span>{label}</span>}
    </label>
  );
});

export interface SwitchProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'type'> {
  label?: ReactNode;
}
export const Switch = forwardRef<HTMLInputElement, SwitchProps>(function Switch({ label, className, id, disabled, ...rest }, ref) {
  const ctx = useFieldCtx();
  return (
    <label className={['hf-switch', className].filter(Boolean).join(' ')}>
      <input type="checkbox" role="switch" ref={ref} id={id ?? ctx?.id} disabled={disabled ?? ctx?.disabled} {...rest} />
      <span className="track" aria-hidden />
      {label != null && <span>{label}</span>}
    </label>
  );
});

export function RadioGroup({
  name,
  value,
  onChange,
  options,
  disabled,
  inline,
  'aria-label': ariaLabel,
}: {
  name: string;
  value: string | null | undefined;
  onChange: (v: string) => void;
  options: SelectOption[];
  disabled?: boolean;
  inline?: boolean;
  'aria-label'?: string;
}) {
  return (
    <div role="radiogroup" aria-label={ariaLabel} className={inline ? 'hf-row' : 'hf-stack'} style={inline ? { gap: 14 } : { gap: 6 }}>
      {options.map((o) => (
        <label key={o.value} className={['hf-check radio', (disabled || o.disabled) && 'disabled'].filter(Boolean).join(' ')}>
          <input
            type="radio"
            name={name}
            value={o.value}
            checked={value === o.value}
            disabled={disabled || o.disabled}
            onChange={() => onChange(o.value)}
          />
          <span className="box" aria-hidden />
          <span>{o.label}</span>
        </label>
      ))}
    </div>
  );
}
